import axios from 'axios';
import { api } from '../../../services/api';
import { logInfo, logError } from '../../../utils/logger';

export type FeedbackRating = 'thumbs_up' | 'thumbs_down';
const MAX_CHAT_FEEDBACK_TEXT_LENGTH = 500;

export interface ChatFeedbackRequest {
  threadId: string;
  messageId: string;
  rating: FeedbackRating;
  text: string;
}

export interface StoredChatFeedback {
  rating: FeedbackRating;
  text: string | null;
  submitted_at: string;
  submitted_by?: string;
}

export interface ChatFeedbackResponse {
  message_id: string;
  feedback: StoredChatFeedback;
}

/**
 * Outcome of a single submit. The chat-v2 backend treats feedback as
 * write-once (Langfuse score submissions create new score records, so we keep
 * the local JSONB in sync by rejecting overwrites). This shape lets the caller
 * distinguish a fresh save from a 409 race where the server already had
 * a prior submission.
 */
export type ChatFeedbackResult =
  | { status: 'submitted'; feedback: StoredChatFeedback }
  | { status: 'already_submitted'; feedback: StoredChatFeedback | null }
  | { status: 'skipped'; reason: 'invalid_ids' | 'missing_text' | 'text_too_long' }
  | { status: 'failed'; error: unknown };

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * Chat-level feedback (thumbs up / thumbs down + required text) on a single
 * assistant message. Backed by ``POST /api/threads/{threadId}/messages/{messageId}/feedback``.
 *
 * Returns a structured result instead of throwing — feedback submission must
 * never break the chat UI, and the caller needs to know about a 409 so it
 * can sync local state with the persisted blob.
 */
export class FinanceFeedbackService {
  async submitChatFeedback(req: ChatFeedbackRequest): Promise<ChatFeedbackResult> {
    const threadId = String(req.threadId || '').trim();
    const messageId = String(req.messageId || '').trim();
    if (!threadId || !messageId || !UUID_RE.test(threadId) || !UUID_RE.test(messageId)) {
      logInfo('FinanceFeedbackService', 'Skipping feedback — invalid thread/message id', {
        threadId,
        messageId,
      });
      return { status: 'skipped', reason: 'invalid_ids' };
    }

    const text = (req.text ?? '').trim();
    if (!text) {
      logInfo('FinanceFeedbackService', 'Skipping feedback — missing comment text', {
        threadId,
        messageId,
      });
      return { status: 'skipped', reason: 'missing_text' };
    }
    if (text.length > MAX_CHAT_FEEDBACK_TEXT_LENGTH) {
      logInfo('FinanceFeedbackService', 'Skipping feedback — comment text too long', {
        threadId,
        messageId,
        length: text.length,
      });
      return { status: 'skipped', reason: 'text_too_long' };
    }
    const body: { rating: FeedbackRating; text: string } = { rating: req.rating, text };

    try {
      const resp = await api.post<ChatFeedbackResponse>(
        `/api/threads/${threadId}/messages/${messageId}/feedback`,
        body,
      );
      logInfo('FinanceFeedbackService', 'Chat feedback submitted', {
        threadId,
        messageId,
        rating: req.rating,
      });
      return { status: 'submitted', feedback: resp.data.feedback };
    } catch (error) {
      // Backend uses 409 with detail = {code: "feedback_already_submitted",
      // message, feedback: <existing-blob>} when the message already has
      // feedback. Treat it as an authoritative sync signal, not an error.
      if (axios.isAxiosError(error) && error.response?.status === 409) {
        const detail = error.response.data?.detail;
        const existing: StoredChatFeedback | null =
          detail && typeof detail === 'object' && detail.feedback ? detail.feedback : null;
        logInfo('FinanceFeedbackService', 'Feedback already submitted (409); syncing local state', {
          threadId,
          messageId,
        });
        return { status: 'already_submitted', feedback: existing };
      }
      logError(
        'FinanceFeedbackService',
        'Chat feedback submission failed',
        error instanceof Error ? error : new Error('Unknown error'),
      );
      console.warn('Feedback submission failed:', error);
      return { status: 'failed', error };
    }
  }
}

export const financeFeedbackService = new FinanceFeedbackService();
